import React from 'react';
import styled, {css} from 'styled-components';

import {CardAccordion, CardAccordionBodyWrapper, CardAccordionHeader} from '../accordion-v2/Accordion.tsx';

/**
 * @description
 * **Timeline Component**
 *
 * The `Timeline` component is used to create a timeline layout. It serves as the parent component for organizing timeline rows and providing styling.
 *
 * **Timeline Row Component**
 *
 * The `Timeline.Row` component is a container used to hold both the header and body content within a timeline. It facilitates the layout and styling of individual timeline entries.
 *
 * **Timeline Header Component**
 *
 * The `Timeline.Header` component represents the main heading or title of each timeline entry. It provides a brief overview of what the timeline entry represents.
 *
 * **Timeline Body Component**
 *
 * The `Timeline.Body` component is an optional feature used to include additional information about each event or milestone within the timeline entry.
 *
 * **TimelineStatus Component**
 *
 * The `TimelineStatus` component is an internal child component of the Timeline. It is automatically included for each timeline row and represents the status of the timeline entry.
 *
 * @example
 * ```jsx
 * import {Timeline} from '../../../../../ui-toolkit/indexExportV2';
 *
 * <Timeline>
 *     <Timeline.Row>
 *         <Timeline.Header>
 *             <b>Text header</b>
 *         </Timeline.Header>
 *         <Timeline.Body>
 *             <p>Text Body</p>
 *         </Timeline.Body>
 *     </Timeline.Row>
 * </Timeline>
 * ```
 */

const TimelineHeader = ({statusColor, children}) => {
    return (
        <TimelineHeaderContainer>
            <TimelineStatus statusColor={statusColor} />
            <TimelineTxt>{children}</TimelineTxt>
        </TimelineHeaderContainer>
    );
};
const TimelineStatus = styled.div`
    position: relative;
    z-index: 2;
    border-radius: 50%;
    display: block;
    background: var(--container-background-color-100);
    height: var(--timeline-status-size);
    width: var(--timeline-status-size);
    flex-shrink: 0;

    ${({statusColor}) => {
        if (statusColor) {
            return css`
                border: 3px solid ${statusColor};
            `;
        } else {
            return css`
                border: 3px solid var(--timeline-border-color);
            `;
        }
    }}
`;

const TimelineRow = styled.div`
    position: relative;
    padding-bottom: var(--space-xsm);
    &:last-of-type {
        &::before {
            display: none;
        }
    }
    &::before {
        content: '';
        width: var(--timeline-border-width);
        height: 100%;
        position: absolute;
        top: var(--timeline-status-size);
        left: var(--timeline-border-position);
        background: var(--timeline-border-color);
        z-index: 1;
    }
`;

export const TimelineHeaderContainer = styled.div`
    align-items: center;
    display: flex;
    gap: var(--space-sm);
`;

const TimelineTxt = styled.div`
    align-items: center;
    display: flex;
    gap: var(--space-sm);
`;

const TimelineBody = styled.div`
    padding-left: calc(var(--timeline-status-size) + var(--space-sm));
`;

export const Timeline = styled.div`
    --timeline-status-size: 12px;
    --timeline-border-width: 2px;
    --timeline-border-position: calc((var(--timeline-status-size) - var(--timeline-border-width)) / 2);
    --timeline-border-color: var(--container-border-color-300);

    //  Condition for when an accordion is placed as a timeline row within the Timeline component.
    &:has(${TimelineHeaderContainer.toString()} ${TimelineTxt.toString()} ${CardAccordion.toString()}) {
        --timeline-padding: 20px;
        margin-top: var(--timeline-padding);
        ${TimelineHeaderContainer} {
            align-items: flex-start;
        }
        ${CardAccordion} {
            position: relative;
            bottom: var(--timeline-padding);
        }
        ${CardAccordion},
        ${TimelineTxt} {
            width: 100%;
        }
    }
    ${({size}) => {
        if (size === 'sm')
            return css`
                ${CardAccordionHeader} {
                    padding-top: var(--space-xsm);
                    padding-bottom: var(--space-xsm);
                }
                ${CardAccordionBodyWrapper} {
                    padding-top: var(--space-sm);
                    padding-bottom: var(--space-xsm);
                }
                ${TimelineRow}::before,
                ${TimelineStatus} {
                    transform: translateY(-7px);
                }
            `;
    }}
`;

/**
 * @description
 * **Timeline Header**
 *
 * This component is required when using the timeline.
 * It not only places the title of the timeline but also sets the timeline status symbol on the left side of the title.
 *
 * @param {object} param0
 *
 * @param {React.ReactNode} [param0.statusColor]
 * This prop is meant to set a color for the timeline status symbol aligned next to the timeline header.
 *
 * @example
 * ```jsx
 *   <Timeline.Header statusColor="var(--primary)">
 *       <b>Text header</b>
 *   </Timeline.Header>
 * ```
 */
Timeline.Header = TimelineHeader;
Timeline.Body = TimelineBody;
Timeline.Row = TimelineRow;
Timeline.Status = TimelineStatus;
